import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';

type NavigateOptions = { replace?: boolean };
type RouterLocation = { pathname: string; search: string; hash: string };
type RouterContextValue = RouterLocation & {
  navigate: (to: string | number, options?: NavigateOptions) => void;
};

const RouterContext = createContext<RouterContextValue | null>(null);
const ParamsContext = createContext<Record<string, string>>({});

function readLocation(): RouterLocation {
  return {
    pathname: window.location.pathname || '/',
    search: window.location.search,
    hash: window.location.hash,
  };
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<RouterLocation>(() => readLocation());

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((to: string | number, options: NavigateOptions = {}) => {
    if (typeof to === 'number') {
      window.history.go(to);
      return;
    }

    if (options.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setLocation(readLocation());
    window.scrollTo({ top: 0, behavior: 'auto' });
    const appScroller = document.querySelector<HTMLElement>('.app-shell-core .app-shell-main');
    if (appScroller) appScroller.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const value = useMemo(() => ({ ...location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const context = useContext(RouterContext);
  if (!context) throw new Error('Nuramend router must be used inside BrowserRouter.');
  return context;
}

export function useNavigate() {
  return useRouter().navigate;
}

export function useLocation() {
  const { pathname, search, hash } = useRouter();
  return { pathname, search, hash };
}

export function RouteParamsProvider({ params, children }: { params: Record<string, string>; children: ReactNode }) {
  return <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>;
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useContext(ParamsContext) as T;
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string };
type NavLinkProps = LinkProps & { exact?: boolean };

export function Link({ to, onClick, target, ...props }: LinkProps) {
  const navigate = useNavigate();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      target === '_blank'
    ) return;
    event.preventDefault();
    navigate(to);
  };

  return <a href={to} target={target} onClick={handleClick} {...props} />;
}

export function NavLink({ to, className = '', exact = false, ...props }: NavLinkProps) {
  const { pathname } = useLocation();
  const active = (exact || to === '/') ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  const classes = [className, active ? 'active' : ''].filter(Boolean).join(' ');
  return <Link to={to} className={classes} aria-current={active ? 'page' : undefined} {...props} />;
}

export function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  if (pattern === '/') return pathname === '/' ? {} : null;

  const names: string[] = [];
  const parts = pattern.split('/').filter(Boolean).map((part) => {
    if (part.startsWith(':')) {
      names.push(part.slice(1));
      return '([^/]+)';
    }
    return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });

  const match = pathname.match(new RegExp(`^/${parts.join('/')}/?$`));
  if (!match) return null;

  return names.reduce<Record<string, string>>((params, name, index) => {
    params[name] = decodeURIComponent(match[index + 1]);
    return params;
  }, {});
}
